import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from '@themesberg/react-bootstrap';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import { BarChart, CircleChart } from '../../components/Charts';
import { rulesApi } from '../../services/api';
import { Routes } from '../../routes';

function ruleToForm(rule) {
  if (!rule) return null;
  return {
    name: rule.name,
    eventType: rule.eventType || '',
    aggregate: rule.condition.aggregate,
    field: rule.condition.field || '',
    groupBy: rule.condition.groupBy,
    window: rule.condition.window,
    operator: rule.condition.operator,
    threshold: String(rule.condition.threshold),
    decision: rule.decision,
    action: rule.action || '',
  };
}

function buildPayload(form) {
  return {
    name: form.name,
    eventType: form.eventType || null,
    condition: {
      aggregate: form.aggregate,
      field: form.aggregate === 'SUM' ? form.field : null,
      groupBy: form.groupBy,
      window: form.window,
      operator: form.operator,
      threshold: Number(form.threshold),
    },
    decision: form.decision,
    action: form.action || null,
  };
}

export default function RuleDetail() {
  const { id } = useParams();
  const [rule, setRule] = useState(null);
  const [stats, setStats] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ruleRes, statsRes, evalRes] = await Promise.all([
        rulesApi.get(id),
        rulesApi.stats(id),
        rulesApi.evaluations(id),
      ]);
      setRule(ruleRes.data);
      setStats(statsRes.data);
      setEvaluations(evalRes.data);
      setForm(ruleToForm(ruleRes.data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const { data } = await rulesApi.update(id, buildPayload(form));
      setRule(data);
      setForm(ruleToForm(data));
      setShowEdit(false);
      setSuccess('Rule updated');
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleRule = async () => {
    setError('');
    try {
      if (rule.enabled) await rulesApi.disable(id);
      else await rulesApi.enable(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading && !rule) {
    return (
      <>
        <PageHeader title="Rule" subtitle="Loading…" />
        <p className="text-gray">Loading rule…</p>
      </>
    );
  }

  if (!rule && error) {
    return (
      <>
        <PageHeader title="Rule not found">
          <Button as={Link} to={Routes.Rules.path} variant="outline-primary">Back to rules</Button>
        </PageHeader>
        <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      </>
    );
  }

  const chartLabels = stats?.byDay?.map((d) => {
    const parts = d.date.split('-');
    return `${parts[1]}/${parts[2]}`;
  }) || [];
  const passedSeries = stats?.byDay?.map((d) => d.passed) || [];
  const breachedSeries = stats?.byDay?.map((d) => d.triggered) || [];

  return (
    <>
      <PageHeader
        title={rule.name}
        subtitle={`${rule.condition.aggregate} on ${rule.condition.groupBy} · ${rule.condition.window}`}
      >
        <Button as={Link} to={Routes.Rules.path} variant="outline-secondary" className="me-2">
          Back
        </Button>
        <Button variant="outline-primary" className="me-2" onClick={() => setShowEdit(true)}>
          Edit
        </Button>
        <Button variant="outline-primary" onClick={toggleRule}>
          {rule.enabled ? 'Disable' : 'Enable'}
        </Button>
      </PageHeader>

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6 className="text-gray mb-1">Total evaluations</h6>
              <h2 className="mb-0">{stats?.total ?? 0}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6 className="text-gray mb-1">Passed</h6>
              <h2 className="mb-0 text-success">{stats?.passed ?? 0}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6 className="text-gray mb-1">Breaches</h6>
              <h2 className="mb-0 text-danger">{stats?.triggered ?? 0}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={4} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6 className="mb-3">Pass vs breach</h6>
              {(stats?.total ?? 0) > 0 ? (
                <div className="d-flex justify-content-center">
                  <CircleChart
                    series={[stats.passed, stats.triggered]}
                    donutWidth={24}
                    wrapperClassName="rule-eval-donut-chart"
                  />
                </div>
              ) : (
                <p className="text-gray small mb-0">No evaluations yet. Run /decide calls that match this rule.</p>
              )}
              <div className="d-flex justify-content-center gap-4 mt-3 small">
                <span><span className="text-success">●</span> Passed ({stats?.passed ?? 0})</span>
                <span><span className="text-danger">●</span> Breach ({stats?.triggered ?? 0})</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8} className="mb-3">
          <Card border="light" className="shadow-sm h-100">
            <Card.Body>
              <h6 className="mb-3">Last 7 days</h6>
              {chartLabels.length > 0 && (stats?.total ?? 0) > 0 ? (
                <BarChart
                  labels={chartLabels}
                  series={[passedSeries, breachedSeries]}
                  chartClassName="ct-golden-section"
                  wrapperClassName="rule-eval-bar-chart"
                />
              ) : (
                <p className="text-gray small mb-0">Daily breakdown appears after the first evaluation.</p>
              )}
              <div className="d-flex gap-4 mt-2 small text-gray">
                <span>Green bars: passed</span>
                <span>Red bars: breach</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={3} className="mb-2">
              <span className="text-gray d-block small">Event type</span>
              <code>{rule.eventType || '*'}</code>
            </Col>
            <Col md={3} className="mb-2">
              <span className="text-gray d-block small">Threshold</span>
              {rule.condition.operator} {rule.condition.threshold}
            </Col>
            <Col md={3} className="mb-2">
              <span className="text-gray d-block small">Decision</span>
              {rule.decision}
            </Col>
            <Col md={3} className="mb-2">
              <span className="text-gray d-block small">Status</span>
              <Badge bg={rule.enabled ? 'success' : 'secondary'}>
                {rule.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm">
        <Card.Body className="p-0">
          <div className="p-3 border-bottom">
            <h6 className="mb-0">Recent API calls</h6>
            <p className="text-gray small mb-0">Each row is one /decide evaluation against this rule.</p>
          </div>
          {evaluations.length === 0 ? (
            <p className="p-4 mb-0 text-gray">No evaluations recorded yet.</p>
          ) : (
            <Table responsive hover className="align-items-center mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Group</th>
                  <th>Compared</th>
                  <th>Threshold</th>
                  <th>Outcome</th>
                  <th>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {evaluations.map((ev) => (
                  <tr key={ev.id}>
                    <td className="small">{new Date(ev.evaluatedAt).toLocaleString()}</td>
                    <td><code>{ev.eventType}</code></td>
                    <td><code>{ev.groupValue}</code></td>
                    <td>
                      {ev.proposedValue != null ? (
                        <span>{ev.proposedValue}{ev.eventValue != null && ev.eventValue !== 0 ? ` (${ev.eventValue} this event)` : ''}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{ev.threshold}</td>
                    <td>
                      <Badge bg={ev.triggered ? 'danger' : 'success'}>
                        {ev.triggered ? 'Breach' : 'Pass'}
                      </Badge>
                    </td>
                    <td>{ev.recorded ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {form ? (
        <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
          <Form onSubmit={handleSave}>
            <Modal.Header closeButton>
              <Modal.Title>Edit rule</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control required value={form.name} onChange={(e) => update('name', e.target.value)} />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Event type</Form.Label>
                  <Form.Control value={form.eventType} onChange={(e) => update('eventType', e.target.value)} placeholder="auth.login_attempt" />
                </Col>
                <Col md={4} className="mb-3">
                  <Form.Label>Aggregate</Form.Label>
                  <Form.Select value={form.aggregate} onChange={(e) => update('aggregate', e.target.value)}>
                    <option value="COUNT">COUNT</option>
                    <option value="SUM">SUM</option>
                  </Form.Select>
                </Col>
                <Col md={4} className="mb-3">
                  <Form.Label>Group by</Form.Label>
                  <Form.Control required value={form.groupBy} onChange={(e) => update('groupBy', e.target.value)} />
                </Col>
                {form.aggregate === 'SUM' ? (
                  <Col md={4} className="mb-3">
                    <Form.Label>Field</Form.Label>
                    <Form.Control required value={form.field} onChange={(e) => update('field', e.target.value)} placeholder="value.amount" />
                  </Col>
                ) : null}
                <Col md={3} className="mb-3">
                  <Form.Label>Window</Form.Label>
                  <Form.Control required value={form.window} onChange={(e) => update('window', e.target.value)} placeholder="PT1M" />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label>Operator</Form.Label>
                  <Form.Select value={form.operator} onChange={(e) => update('operator', e.target.value)}>
                    <option value=">">&gt;</option>
                    <option value=">=">&gt;=</option>
                    <option value="<">&lt;</option>
                    <option value="<=">&lt;=</option>
                    <option value="==">==</option>
                    <option value="!=">!=</option>
                  </Form.Select>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label>Threshold</Form.Label>
                  <Form.Control required type="number" min="0" value={form.threshold} onChange={(e) => update('threshold', e.target.value)} />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label>Decision</Form.Label>
                  <Form.Select value={form.decision} onChange={(e) => update('decision', e.target.value)}>
                    <option value="ALLOW">ALLOW</option>
                    <option value="REVIEW">REVIEW</option>
                    <option value="BLOCK">BLOCK</option>
                  </Form.Select>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Action</Form.Label>
                  <Form.Control value={form.action} onChange={(e) => update('action', e.target.value)} placeholder="REQUIRE_COOLDOWN" />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button variant="primary" type="submit">Save changes</Button>
            </Modal.Footer>
          </Form>
        </Modal>
      ) : null}
    </>
  );
}
